import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ShoppingCart, DollarSign, TrendingUp, AlertTriangle, Clock, Check, Truck, Package } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const statusConfig = {
  new: { label: 'Novo', color: 'status-new' },
  confirmed: { label: 'Confirmado', color: 'status-confirmed' },
  preparing: { label: 'Preparando', color: 'status-preparing' },
  out_for_delivery: { label: 'Saiu Entrega', color: 'status-delivery' },
  ready_for_pickup: { label: 'Pronto Retirada', color: 'status-delivery' },
  completed: { label: 'Concluído', color: 'status-completed' },
  cancelled: { label: 'Cancelado', color: 'status-cancelled' }
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, lowStockRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/orders?status=new`),
        axios.get(`${API}/stock/low`)
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
      setLowStock(lowStockRes.data.slice(0, 5));
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success('Status atualizado');
      fetchData();
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-adegga" data-testid="stat-orders">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Pedidos Hoje</p>
                  <p className="text-2xl font-bold text-white">{stats?.today_orders || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <ShoppingCart className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga" data-testid="stat-revenue">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Faturamento Hoje</p>
                  <p className="text-2xl font-bold text-[#ff6b00]">
                    R$ {(stats?.today_revenue || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ff6b00]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#ff6b00]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga" data-testid="stat-ticket">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Ticket Médio</p>
                  <p className="text-2xl font-bold text-white">
                    R$ {(stats?.ticket_medio || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga" data-testid="stat-pending">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Pedidos Pendentes</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats?.pending_orders || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card className="card-adegga">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-white">Novos Pedidos</CardTitle>
              <Link to="/app/pedidos">
                <Button variant="ghost" size="sm" className="text-[#ff6b00]">Ver todos</Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentOrders.length === 0 ? (
                <p className="text-white/40 text-center py-4">Nenhum pedido novo</p>
              ) : (
                recentOrders.map(order => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 bg-[#0f0f15] rounded-lg"
                    data-testid={`order-${order.id}`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white">#{order.order_number}</span>
                        <Badge className={statusConfig[order.status]?.color}>
                          {statusConfig[order.status]?.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-white/60">{order.customer_name}</p>
                      <p className="text-sm text-[#ff6b00] font-bold">R$ {order.total.toFixed(2)}</p>
                    </div>
                    <div className="flex gap-2">
                      {order.status === 'new' && (
                        <Button
                          size="sm"
                          onClick={() => handleUpdateStatus(order.id, 'confirmed')}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Low Stock Alert */}
          <Card className="card-adegga">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Estoque Baixo
              </CardTitle>
              <Link to="/app/estoque">
                <Button variant="ghost" size="sm" className="text-[#ff6b00]">Ver todos</Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {lowStock.length === 0 ? (
                <p className="text-white/40 text-center py-4">Estoque OK</p>
              ) : (
                lowStock.map(product => (
                  <div
                    key={product.id}
                    className="flex items-center justify-between p-3 bg-[#0f0f15] rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-white">{product.name}</p>
                      <p className="text-sm text-white/60">Mínimo: {product.stock_alert_min}</p>
                    </div>
                    <Badge className={product.stock_quantity <= 0 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}>
                      {product.stock_quantity} un
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Low Stock Count Warning */}
        {stats?.low_stock_count > 0 && (
          <Card className="card-adegga border-yellow-500/30 bg-yellow-500/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
                <div>
                  <p className="font-bold text-white">Atenção ao Estoque</p>
                  <p className="text-sm text-white/60">
                    {stats.low_stock_count} produto(s) com estoque baixo ou zerado
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
